/**
 * Initialize a WebGL context for rendering with options for debug wrappers etc.
 * @module
 */
import { ENVIRONMENTS } from "common/constants/env";
import { getClientEnvironment } from "common/utils/env";
import { UnsupportedRendererError, WebGLUnavailableError } from "common/utils/fatalError";
import { productLog } from "common/utils/logging";
import { WebGLDebugUtils } from "./webgl-debug";
import type WebGLContextResourceTracker from "./WebGLContextResourceTracker";
import { WebGLMultiContextResourceTracker } from "./WebGLContextResourceTracker";

const rendererBlacklist = {
  // "Google SwiftShader": true, // temporarily disabled for experimental purposes
  "Intel(R) Iris(TM) Graphics 540": true,
};

const RESOURCE_TRACKER_REPORT_INTERVAL = 5000;
const ENABLE_WEBGL_RESOURCE_LOGGING = false;
let resourceTrackerReportInterval: number | undefined = undefined;

export interface GLDefaultFramebufferOptions {
  antialias: boolean;
  depthStencil: boolean;
}

export interface GLContextInitOptions {
  framebufferOptions: GLDefaultFramebufferOptions;
  throwOnGLError: boolean;
  logGLResourceUsage: boolean;

  allowWebGL1?: boolean; // Should only be used for diagnostic purposes. The renderers only support WebGL 2.0.

  // If set, will be used as a whitelist to limit supported WebGL extensions.
  limitWebGLExtensions?: string[];

  label: string; // For debugging.
}

export interface InitializedGLContext {
  canvas: HTMLCanvasElement;
  glContext: WebGLRenderingContext | WebGL2RenderingContext;
  glContextAttributes: WebGLContextAttributes;
  hasWebGL2: boolean;
  resourceTracker: WebGLContextResourceTracker;
  resourceTrackerLogInterval: number | undefined;
}

export function initWebGLContext(options: GLContextInitOptions): InitializedGLContext {
  const depthStencil = options.framebufferOptions.depthStencil;
  const glContextAttributes: WebGLContextAttributes = {
    antialias: options.framebufferOptions.antialias,
    powerPreference: "high-performance",
    depth: depthStencil,
    stencil: depthStencil,

    // Important to keep false to avoid an extra full screen copy operation on each frame.
    preserveDrawingBuffer: false,

    // Enabling alpha may increase performance slightly on some platforms, since they might need to emulate the mode where alpha is disabled.
    alpha: true,
  };

  const canvas = document.createElement("canvas");
  let glContext: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  let hasWebGL2 = false;

  glContext = canvas.getContext("webgl2", glContextAttributes);
  if (glContext) {
    hasWebGL2 = true;
  }

  if (glContext === null && (options.allowWebGL1 ?? false)) {
    glContext = canvas.getContext("webgl", glContextAttributes);
  }

  if (glContext === null) {
    throw new WebGLUnavailableError();
  }

  if (glContext.isContextLost()) {
    throw new Error("WebGL context is lost on initialization");
  }

  if (options.throwOnGLError || options.limitWebGLExtensions !== undefined) {
    if (options.limitWebGLExtensions !== undefined) {
      productLog("Limiting WebGL extensions to whitelist for testing: ", options.limitWebGLExtensions);
    }
    let throwOnGLError;
    if (options.throwOnGLError) {
      throwOnGLError = function (err, funcName) {
        throw WebGLDebugUtils.glEnumToString(err) + " was caused by call to: " + funcName;
      };
    }
    glContext = WebGLDebugUtils.makeDebugContext(glContext, throwOnGLError, undefined, options.limitWebGLExtensions) as
      | WebGLRenderingContext
      | WebGL2RenderingContext;
  }

  const debugInfo = glContext.getExtension("WEBGL_debug_renderer_info");
  let gpuVendorInfo = {};
  if (debugInfo) {
    const vendorName = glContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const rendererName = glContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    if (rendererBlacklist[rendererName]) {
      throw new UnsupportedRendererError(rendererName, vendorName);
    }
    gpuVendorInfo = { vendorName, rendererName };
  }

  const resourceTracker = WebGLMultiContextResourceTracker.addContext(glContext, options.label, gpuVendorInfo);

  // Report for this specific context
  let singleContextResourceTrackerReportInterval: number | undefined = undefined;
  if (options.logGLResourceUsage) {
    singleContextResourceTrackerReportInterval = window.setInterval(function () {
      productLog(resourceTracker.report());
    }, 5000);
  }

  // Report for all contexts
  const reportWebGLResources = getClientEnvironment().server === ENVIRONMENTS.local && ENABLE_WEBGL_RESOURCE_LOGGING;
  if (reportWebGLResources && !resourceTrackerReportInterval) {
    resourceTrackerReportInterval = window.setInterval(function () {
      productLog(WebGLMultiContextResourceTracker.report());
    }, RESOURCE_TRACKER_REPORT_INTERVAL);
  }

  return {
    canvas,
    glContext,
    glContextAttributes,
    hasWebGL2,
    resourceTracker,
    resourceTrackerLogInterval: singleContextResourceTrackerReportInterval,
  };
}
